import styles from 'css/src/components/app/template-preview/TemplateCategorySelectionList.module.scss';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Template } from 'js/src/client';
import TemplateCategory from 'js/src/components/app/template-preview/TemplateCategory';
import TemplateCategoryPreviewList from 'js/src/components/app/template-preview/TemplateCategoryPreviewList';
import { TemplateSearchButton } from 'js/src/components/app/template-preview/TemplateSearchButton';
import TemplateSearchResultsList from 'js/src/components/app/template-preview/TemplateSearchResultsList';
import {
    getActiveTemplateCategoryId,
    getTemplateCategoryIds,
    getTemplateSearchQuery,
    setActiveTemplateCategoryId,
} from 'js/src/data/templates';
import type { ImmutableMap, TemplateCategoryId } from 'js/src/libs/consts';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

const STICKY_SELECTOR_OFFSET = 100;

function findScrollableAncestor(node: HTMLElement | null): HTMLElement | null {
    let current = node?.parentElement ?? null;
    while (current) {
        const overflowY = getComputedStyle(current).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }
    return null;
}

type TemplateCategorySelectionListProps = {
    onTemplateClick: (template: ImmutableMap<Template>) => void;
    selectedTemplateId?: string;
    hidePreviewButton?: boolean;
    /**
     * Optional content rendered between the category selector and the
     * template grid (e.g. a dismissible callout above the tiles).
     */
    belowCategorySelector?: React.ReactNode;
    useWindowScroll?: boolean;
};

type ScrollHost = {
    target: HTMLElement | Window;
    getTop: () => number;
    getBottom: () => number;
    getScrollTop: () => number;
    scrollTo: (top: number) => void;
};

const TemplateCategorySelectionList = React.memo(function TemplateCategorySelectionList({
    onTemplateClick,
    selectedTemplateId,
    hidePreviewButton,
    belowCategorySelector,
    useWindowScroll = false,
}: TemplateCategorySelectionListProps) {
    const dispatch = useDispatch<AppDispatch>();
    const templateCategoryIds = useSelector((state: RootState) => getTemplateCategoryIds(state));
    const activeTemplateCategoryId = useSelector((state: RootState) =>
        getActiveTemplateCategoryId(state),
    );
    const searchQuery = useSelector((state: RootState) => getTemplateSearchQuery(state));
    const rootRef = React.useRef<HTMLDivElement>(null);
    const [scrollAncestor, setScrollAncestor] = React.useState<HTMLElement | null>(null);
    const templateCategoryRef = React.useRef(new Map<TemplateCategoryId, HTMLElement>());
    const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);

    const getTemplateCategoryRefs = React.useCallback(() => {
        return templateCategoryRef.current;
    }, []);

    React.useLayoutEffect(() => {
        if (useWindowScroll) return;
        setScrollAncestor(findScrollableAncestor(rootRef.current));
    }, [useWindowScroll]);

    const scrollHost = React.useMemo<ScrollHost | null>(() => {
        if (typeof window === 'undefined') return null;
        if (useWindowScroll) {
            return {
                target: window,
                getTop: () => 0,
                getBottom: () => window.innerHeight,
                getScrollTop: () => window.scrollY,
                scrollTo: (top) => window.scrollTo({ top, behavior: 'smooth' }),
            };
        }
        if (!scrollAncestor) return null;
        return {
            target: scrollAncestor,
            getTop: () => scrollAncestor.getBoundingClientRect().top,
            getBottom: () => scrollAncestor.getBoundingClientRect().bottom,
            getScrollTop: () => scrollAncestor.scrollTop,
            scrollTo: (top) => scrollAncestor.scrollTo({ top, behavior: 'smooth' }),
        };
    }, [useWindowScroll, scrollAncestor]);

    React.useEffect(() => {
        if (!scrollHost) return;

        const handleScroll = () => {
            const hostTop = scrollHost.getTop();
            const hostBottom = scrollHost.getBottom();
            const categoryRefs = getTemplateCategoryRefs();
            let closestCategory: TemplateCategoryId | null = null;
            let minDistance = Infinity;

            categoryRefs.forEach((element, categoryId) => {
                const rect = element.getBoundingClientRect();
                const distance = Math.abs(rect.top - hostTop - STICKY_SELECTOR_OFFSET);

                if (distance < minDistance && rect.top < hostBottom) {
                    minDistance = distance;
                    closestCategory = categoryId;
                }
            });

            if (closestCategory !== null && closestCategory !== activeTemplateCategoryId) {
                dispatch(setActiveTemplateCategoryId({ categoryId: closestCategory }));
            }
        };

        scrollHost.target.addEventListener('scroll', handleScroll);
        return () => {
            scrollHost.target.removeEventListener('scroll', handleScroll);
        };
    }, [activeTemplateCategoryId, dispatch, getTemplateCategoryRefs, scrollHost]);

    const onTemplateCategoryClick = React.useCallback(
        (evt: React.MouseEvent, templateCategoryId: TemplateCategoryId) => {
            evt.stopPropagation();
            evt.preventDefault();
            dispatch(setActiveTemplateCategoryId({ categoryId: templateCategoryId }));

            const element = getTemplateCategoryRefs()?.get(templateCategoryId);

            if (element && scrollHost) {
                const elementRect = element.getBoundingClientRect();
                const relativeTop = elementRect.top - scrollHost.getTop();
                const offsetPosition =
                    scrollHost.getScrollTop() + relativeTop - STICKY_SELECTOR_OFFSET;

                scrollHost.scrollTo(offsetPosition);
            }
        },
        [dispatch, getTemplateCategoryRefs, scrollHost],
    );

    const shouldShowCategorySelector = templateCategoryIds.size > 1;
    const hasSearchQuery = searchQuery.trim().length > 0;

    return (
        <div ref={rootRef} className={styles['template-category-selection-list']}>
            {shouldShowCategorySelector && (
                <div className={`${styles['template-category-selector-container']} py-l5-8`}>
                    <div className={`${styles['template-category-selector']} px-l5-32 py-l5-8`}>
                        <TemplateSearchButton onExpandedChange={setIsSearchExpanded} />
                        {!isSearchExpanded &&
                            templateCategoryIds.map((categoryId) => (
                                <TemplateCategory
                                    key={categoryId}
                                    categoryId={categoryId}
                                    onClick={onTemplateCategoryClick}
                                    activeTemplateCategoryId={activeTemplateCategoryId}
                                />
                            ))}
                    </div>
                </div>
            )}

            {belowCategorySelector && (
                <div className="px-l5-32 pt-l5-16">{belowCategorySelector}</div>
            )}
            <div className={`${styles['template-content-container']} p-l5-32 pb-l5-64`}>
                <div className={styles['template-content']}>
                    {hasSearchQuery ? (
                        <TemplateSearchResultsList
                            onTemplateClick={onTemplateClick}
                            selectedTemplateId={selectedTemplateId}
                            hidePreviewButton={hidePreviewButton}
                        />
                    ) : (
                        <TemplateCategoryPreviewList
                            onTemplateClick={onTemplateClick}
                            getTemplateCategoryRefs={getTemplateCategoryRefs}
                            selectedTemplateId={selectedTemplateId}
                            hidePreviewButton={hidePreviewButton}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

export default TemplateCategorySelectionList;
